import React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

interface Review {
  rating: number;
  title: string;
  content: string;
  author: string;
}

const reviews: Review[] = [
    {
      rating: 5.0,
      title: "Koken op inductie, dankzij Elektraaanleggen.nl!",
      content:
        "We hebben een nieuwe inductiekookplaat gekocht, maar de aansluiting was een probleem. Elektraaanleggen.nl heeft het perfect opgelost. Alles werkt nu super, en we zijn heel blij met het resultaat.",
      author: "Linda S.",
    },
    {
      rating: 4.5,
      title: "Onze oude woning weer helemaal up-to-date!",
      content:
        "We hebben ons oude huis laten renoveren, en de elektra was echt een grote klus. Elektraaanleggen.nl heeft ons goed geadviseerd en alles perfect uitgevoerd. Het resultaat is echt top!",
      author: "Peter en Anouk",
    },
    {
      rating: 5.0,
      title: "Krachtstroom voor mijn werkplaats, perfect geregeld!",
      content:
        "Ik heb een 3-fase aansluiting nodig voor mijn nieuwe werkplaats. Elektraaanleggen.nl heeft dit snel en vakkundig geregeld. Ik ben echt heel tevreden over de service.",
      author: "Martijn K.",
    },
    {
      rating: 4.0,
      title: "Elektra in de aanbouw, goed en betaalbaar.",
      content:
        "We hebben een aanbouw laten plaatsen en hadden elektra nodig. Elektraaanleggen.nl heeft het goed gedaan voor een nette prijs. Prima werk geleverd!",
      author: "Sander en Kim",
    },
    {
      rating: 5.0,
      title: "Mijn elektrische auto laadt nu thuis op!",
      content:
        "De installatie van mijn laadpaal is vlekkeloos verlopen. Elektraaanleggen.nl heeft alles professioneel geïnstalleerd en me goed uitgelegd hoe alles werkt. Top gedaan!",
      author: "Erik van Dijk",
    },
    {
      rating: 4.5,
      title: "Zonnepanelen aansluiten, goed advies gekregen.",
      content:
        "We hebben zonnepanelen laten installeren en Elektraaanleggen.nl heeft de aansluiting verzorgd. We kregen goed advies en de installatie is netjes gedaan.",
      author: "De familie Jansen",
    },
    {
      rating: 5.0,
      title: "Perilex aansluiting voor mijn nieuwe oven, perfect!",
      content:
        "Ik had een Perilex aansluiting nodig voor mijn nieuwe oven. De elektricien van Elektraaanleggen.nl was supervriendelijk en heeft het perfect gedaan. Ik ben heel blij!",
      author: "Laura V.",
    },
    {
      rating: 4.8,
      title: "Snelle hulp bij stroomuitval, wat een opluchting!",
      content:
        "We hadden een stroomstoring en Elektraaanleggen.nl was er snel bij. De elektricien was erg vriendelijk en heeft het probleem snel opgelost. Echt een opluchting!",
      author: "Mw. de Graaf",
    },
    {
      rating: 5.0,
      title: "Goed advies over elektrische veiligheid voor mijn bedrijf.",
      content:
        "Ik heb advies gevraagd over de elektrische veiligheid in mijn bedrijfspand. Elektraaanleggen.nl heeft me echt goed geholpen met deskundig advies en bruikbare tips. Heel fijn!",
      author: "Directie Bakkerij Smits",
    },
];

export function Reviews() {
  const [currentIndex, setCurrentIndex] = React.useState(0);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = React.useState(false);
  const [startX, setStartX] = React.useState(0);
  const [scrollLeft, setScrollLeft] = React.useState(0);
  const [maxSlides, setMaxSlides] = React.useState(0);
  const [scrollAccumulator, setScrollAccumulator] = React.useState(0);
  const [touchStart, setTouchStart] = React.useState(0);
  const cardWidth = 348 + 20; // Card width + gap

  React.useEffect(() => {
    setMaxSlides(reviews.length - 1);
  }, []);

  const handleWheel = (e: WheelEvent) => {
    if (!containerRef.current) return;

    if (e.deltaX !== 0 || e.shiftKey) {
      e.preventDefault();

      // Update scroll accumulator
      const newAccumulator = scrollAccumulator + (e.deltaX || e.deltaY);
      setScrollAccumulator(newAccumulator);

      // Check if we've accumulated enough scroll to move one card
      const threshold = 100; // Adjust this value to control sensitivity
      if (Math.abs(newAccumulator) >= threshold) {
        const direction = newAccumulator > 0 ? 1 : -1;
        const newIndex = currentIndex + direction;
        setCurrentIndex(Math.max(0, Math.min(reviews.length - 1, newIndex)));
        setScrollAccumulator(0); // Reset accumulator after scrolling
      }
    }
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStart(e.touches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!touchStart) return;

    const touchEnd = e.touches[0].clientX;
    const diff = touchStart - touchEnd;

    // Only prevent default if significant movement detected
    if (Math.abs(diff) > 5) {
      e.preventDefault();
    }

    // Threshold for swipe
    if (Math.abs(diff) > 50) {
      if (diff > 0 && currentIndex < reviews.length - 1) {
        setCurrentIndex((prev) => prev + 1);
      } else if (diff < 0 && currentIndex > 0) {
        setCurrentIndex((prev) => prev - 1);
      }
      setTouchStart(0);
    }
  };

  const handleTouchEnd = () => {
    setTouchStart(0);
  };

  // Debounce the wheel event to prevent rapid scrolling
  const debounceTimeout = React.useRef<NodeJS.Timeout>();
  const handleDebouncedWheel = (e: WheelEvent) => {
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }

    debounceTimeout.current = setTimeout(() => {
      handleWheel(e);
    }, 50); // Adjust debounce delay as needed
  };

  React.useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    container.addEventListener("wheel", handleDebouncedWheel, {
      passive: false,
    });
    return () => {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
      container.removeEventListener("wheel", handleDebouncedWheel);
    };
  }, [currentIndex]);

  // Clean up debounce timeout on unmount
  React.useEffect(() => {
    return () => {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
    };
  }, []);

  const handleDrag = (e: React.MouseEvent) => {
    if (!isDragging) return;
    e.preventDefault();

    const x = e.pageX - containerRef.current!.offsetLeft;
    const walk = x - startX;

    // Calculate the nearest card index based on drag distance
    const dragThreshold = cardWidth / 2;
    if (Math.abs(walk) > dragThreshold) {
      const direction = walk > 0 ? -1 : 1;
      const newIndex = currentIndex + direction;
      setCurrentIndex(Math.max(0, Math.min(reviews.length - 1, newIndex)));
      setIsDragging(false);
      setStartX(x); // Update start position for next drag
    }
  };

  React.useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    container.addEventListener("wheel", handleWheel, { passive: false });
    return () => {
      container.removeEventListener("wheel", handleWheel);
    };
  }, [currentIndex]);

  const handlePrevious = () => {
    setCurrentIndex((prev) => Math.max(0, prev - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => Math.min(reviews.length - 1, prev + 1));
  };

  return (
    <div className="my-20 md:mb-32">
      <section className="mx-auto w-[calc(100%-2.5rem)] max-w-[1260px] relative">
        <div className="flex justify-between items-center mb-6">
          <h3 className="font-bold text-black leading-[1.25] text-[24px] lg:text-[36px] text-[#1a2b3b]">
            Wat zeggen onze klanten?
          </h3>
          <div className="hidden items-center md:flex">
            <button
              onClick={handlePrevious}
              disabled={currentIndex === 0}
              className="flex items-center transition-opacity duration-150 w-10 h-10 hover:opacity-50 active:opacity-20 disabled:opacity-40"
            >
              <ChevronLeft className="h-10 w-10" />
            </button>
            <button
              onClick={handleNext}
              disabled={currentIndex >= maxSlides}
              className="flex items-center transition-opacity duration-150 w-10 h-10 hover:opacity-50 active:opacity-20 disabled:opacity-40"
            >
              <ChevronRight className="h-10 w-10" />
            </button>
          </div>
        </div>
      </section>

      <div className="w-full overflow-hidden">
        <div
          ref={containerRef}
          className="w-full mb-4 cursor-grab active:cursor-grabbing select-none"
          onMouseDown={(e) => {
            setIsDragging(true);
            setStartX(e.pageX - containerRef.current!.offsetLeft);
            setScrollLeft(currentIndex * cardWidth);
          }}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onMouseUp={() => {
            setIsDragging(false);
          }}
          onMouseLeave={() => {
            setIsDragging(false);
          }}
          onMouseMove={handleDrag}
        >
          <div
            className="flex gap-5 transition-transform duration-300 ease-in-out pl-[calc(max((100vw-1260px)/2,20px))] select-none"
            style={{ transform: `translateX(-${currentIndex * (348 + 20)}px)` }}
          >
            {/* Trustpilot Card */}
            <div className="flex flex-col items-center justify-between lg:pt-[120px] pt-[50px] min-w-[248px] h-[300px] lg:min-w-[348px] lg:h-[400px] px-6 py-8 bg-white border border-gray-200 rounded-xl">
              <div className="flex flex-col items-center">
                <img
                  alt="trustpilot-logo"
                  width="160"
                  height="40"
                  className="mb-4"
                  src="/images/trustpilot.svg"
                />
                <div className="mb-1 flex flex-col items-center">
                  <div className="flex flex-row items-center">
                    <div className="text-[24px] font-medium text-[#1a2b3b]">
                      4.7
                    </div>
                  </div>
                  <div className="text-[14px] font-medium text-[#B9B9B9] mt-[-5px]">
                    Uitstekend!
                  </div>
                </div>
                <div className="flex flex-row gap-[2px]">
                  {[1, 2, 3, 4, 4.2].map((_, i) => (
                    <svg
                      key={i}
                      width="14"
                      height="14"
                      viewBox="0 0 12 12"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M5.40747 1.42824C5.59379 0.857252 6.40621 0.857252 6.59254 1.42824L7.40436 3.91608C7.48769 4.17141 7.72693 4.34432 7.9969 4.34432H10.6259C11.2291 4.34432 11.4802 5.11172 10.9924 5.46461L8.86399 7.00433C8.64596 7.16206 8.55473 7.44136 8.63794 7.69637L9.45055 10.1866C9.63683 10.7574 8.97957 11.2317 8.49153 10.8787L6.3665 9.34134C6.14802 9.18329 5.85199 9.18329 5.63351 9.34134L3.50848 10.8787C3.02043 11.2317 2.36318 10.7574 2.54945 10.1866L3.36206 7.69637C3.44528 7.44136 3.35405 7.16206 3.13602 7.00433L1.00765 5.46461C0.519842 5.11172 0.770888 4.34432 1.37414 4.34432H4.0031C4.27307 4.34432 4.51232 4.17141 4.59564 3.91608L5.40747 1.42824Z"
                        fill="#FDBB00"
                        stroke="#FDBB00"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  ))}
                </div>
              </div>
              <div className="text-[14px] font-medium">128 reviews</div>
            </div>

            {/* Review Cards */}
            {reviews.map((review, index) => (
              <div
                key={index}
                className="min-w-[248px] h-[300px] lg:min-w-[348px] lg:h-[400px] px-6 py-8 bg-white border border-gray-200 rounded-xl"
              >
                <div className="flex flex-col h-full justify-between gap-6">
                  <div className="flex flex-col">
                    <div className="flex flex-row w-full justify-between items-center mb-3">
                      <div className="flex gap-2 items-center">
                        <div className="text-[14px] font-medium">
                          {review.rating.toFixed(1)}
                        </div>
                        <div className="flex flex-row gap-1">
                          {[1, 2, 3, 4, 5].map((_, i) => (
                            <svg
                              key={i}
                              width="16"
                              height="16"
                              viewBox="0 0 12 12"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M5.40747 1.42824C5.59379 0.857252 6.40621 0.857252 6.59254 1.42824L7.40436 3.91608C7.48769 4.17141 7.72693 4.34432 7.9969 4.34432H10.6259C11.2291 4.34432 11.4802 5.11172 10.9924 5.46461L8.86399 7.00433C8.64596 7.16206 8.55473 7.44136 8.63794 7.69637L9.45055 10.1866C9.63683 10.7574 8.97957 11.2317 8.49153 10.8787L6.3665 9.34134C6.14802 9.18329 5.85199 9.18329 5.63351 9.34134L3.50848 10.8787C3.02043 11.2317 2.36318 10.7574 2.54945 10.1866L3.36206 7.69637C3.44528 7.44136 3.35405 7.16206 3.13602 7.00433L1.00765 5.46461C0.519842 5.11172 0.770888 4.34432 1.37414 4.34432H4.0031C4.27307 4.34432 4.51232 4.17141 4.59564 3.91608L5.40747 1.42824Z"
                                fill="#FDBB00"
                                stroke="#FDBB00"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          ))}
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-col gap-2 mb-1">
                      <div className="text-[14px] md:text-[18px] font-medium text-[#1a2b3b]">
                        {review.title}
                      </div>
                      <div className="text-[14px] md:text-[16px] text-[#1a2b3b] line-clamp-6">
                        {review.content}
                      </div>
                    </div>
                  </div>
                  <div className="text-[14px] md:text-[16px] font-medium text-[#1a2b3b]">
                    — {review.author}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Mobile Pagination Dots */}
        <div className="hidden w-full flex items-center justify-center gap-2 md:hidden">
          {reviews.map((_, index) => (
            <div
              key={index}
              className={`h-2 w-2 rounded transition-all duration-150 ${
                index === currentIndex ? "bg-[#31b6d5]" : "bg-gray-200"
              }`}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
